/**
 * FileHandler Utility
 * 
 * Handles file operations for JSON files including drag and drop,
 * file validation, reading, and error handling.
 */

export class FileHandler {
  constructor(options = {}) {
    this.options = {
      maxFileSize: 10 * 1024 * 1024, // 10MB default
      allowedExtensions: ['.json', '.txt'],
      allowedMimeTypes: ['application/json', 'text/plain', 'text/json'],
      ...options
    }
  }

  /**
   * Validate if a file is acceptable for JSON processing
   * @param {File} file - The file to validate
   * @returns {Object} Validation result with isValid and error properties
   */
  validateFile(file) {
    if (!file) {
      return {
        isValid: false,
        error: 'No file provided',
        errorType: 'NO_FILE'
      }
    }

    // Check file size
    if (file.size > this.options.maxFileSize) {
      const maxSizeMB = Math.round(this.options.maxFileSize / (1024 * 1024))
      return {
        isValid: false,
        error: `File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size of ${maxSizeMB}MB`,
        errorType: 'FILE_TOO_LARGE'
      }
    }

    // Check file extension
    const fileExtension = this.getFileExtension(file.name).toLowerCase()
    if (!this.options.allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File type "${fileExtension}" is not supported. Allowed types: ${this.options.allowedExtensions.join(', ')}`,
        errorType: 'INVALID_EXTENSION'
      }
    }

    // Check MIME type if available
    if (file.type && !this.options.allowedMimeTypes.includes(file.type)) {
      // Only warn, don't reject - some systems don't set MIME types correctly
      console.warn(`File MIME type "${file.type}" is not in allowed list, but proceeding anyway`)
    }

    return {
      isValid: true,
      error: null,
      errorType: null
    }
  }

  /**
   * Read a file and return its content as text
   * @param {File} file - The file to read
   * @returns {Promise<Object>} Promise resolving to result with success, content, and error properties
   */
  async readFile(file) {
    return new Promise((resolve) => {
      // Validate file first
      const validation = this.validateFile(file)
      if (!validation.isValid) {
        resolve({
          success: false,
          content: null,
          error: validation.error,
          errorType: validation.errorType,
          fileInfo: this.getFileInfo(file)
        })
        return
      }

      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const content = event.target.result

          // Basic content validation
          if (typeof content !== 'string') {
            resolve({
              success: false,
              content: null,
              error: 'File content could not be read as text',
              errorType: 'READ_ERROR',
              fileInfo: this.getFileInfo(file)
            })
            return
          }

          // Check if content is empty
          if (!content.trim()) {
            resolve({
              success: false,
              content: null,
              error: 'File is empty',
              errorType: 'EMPTY_FILE',
              fileInfo: this.getFileInfo(file)
            })
            return
          }

          resolve({
            success: true,
            content: content,
            error: null,
            errorType: null,
            fileInfo: this.getFileInfo(file)
          })

        } catch (error) {
          resolve({
            success: false,
            content: null,
            error: `Failed to process file: ${error.message}`,
            errorType: 'PROCESSING_ERROR',
            fileInfo: this.getFileInfo(file)
          })
        }
      }

      reader.onerror = () => {
        resolve({
          success: false,
          content: null,
          error: 'Failed to read file. The file may be corrupted or inaccessible.',
          errorType: 'READ_ERROR',
          fileInfo: this.getFileInfo(file)
        })
      }

      reader.onabort = () => {
        resolve({
          success: false,
          content: null,
          error: 'File reading was aborted',
          errorType: 'READ_ABORTED',
          fileInfo: this.getFileInfo(file)
        })
      }

      // Start reading the file as text
      reader.readAsText(file)
    })
  }

  /**
   * Process multiple files and return the first valid JSON file
   * @param {FileList|File[]} files - Array or FileList of files
   * @returns {Promise<Object>} Promise resolving to result with success, content, and error properties
   */
  async processFiles(files) {
    if (!files || files.length === 0) {
      return {
        success: false,
        content: null,
        error: 'No files provided',
        errorType: 'NO_FILES'
      }
    }

    // Convert FileList to Array if needed
    const fileArray = Array.from(files)

    // Try to find and process the first valid file
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i]
      const result = await this.readFile(file)

      if (result.success) {
        return {
          ...result,
          processedFileIndex: i,
          totalFiles: fileArray.length
        }
      }

      // If this is the last file and we haven't found a valid one, return the error
      if (i === fileArray.length - 1) {
        return {
          ...result,
          processedFileIndex: i,
          totalFiles: fileArray.length
        }
      }
    }

    // Fallback (shouldn't reach here)
    return {
      success: false,
      content: null,
      error: 'No valid files found',
      errorType: 'NO_VALID_FILES',
      totalFiles: fileArray.length
    }
  }

  /**
   * Check if drag event contains files
   * @param {DragEvent} event - The drag event
   * @returns {boolean} True if event contains files
   */
  hasFiles(event) {
    return event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files.length > 0
  }

  /**
   * Check if drag event contains potentially valid files
   * @param {DragEvent} event - The drag event
   * @returns {boolean} True if event contains potentially valid files
   */
  hasValidFiles(event) {
    if (!this.hasFiles(event)) {
      return false
    }

    // Check if any of the files have valid extensions
    const files = Array.from(event.dataTransfer.files)
    return files.some(file => {
      const extension = this.getFileExtension(file.name).toLowerCase()
      return this.options.allowedExtensions.includes(extension)
    })
  }

  /**
   * Get file extension from filename
   * @param {string} filename - The filename
   * @returns {string} File extension including the dot
   */
  getFileExtension(filename) {
    if (!filename || typeof filename !== 'string') {
      return ''
    }
    const lastDotIndex = filename.lastIndexOf('.')
    return lastDotIndex === -1 ? '' : filename.substring(lastDotIndex)
  }

  /**
   * Format file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Get file information object
   * @param {File} file - The file
   * @returns {Object} File information
   */
  getFileInfo(file) {
    if (!file) return null

    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      extension: this.getFileExtension(file.name),
      formattedSize: this.formatFileSize(file.size)
    }
  }

  /**
   * Create a user-friendly error message
   * @param {string} error - The error message
   * @param {string} errorType - The error type
   * @param {Object} fileInfo - File information
   * @returns {string} User-friendly error message
   */
  createUserFriendlyError(error, errorType, fileInfo) {
    const fileName = fileInfo ? fileInfo.name : 'file'

    switch (errorType) {
      case 'NO_FILE':
        return 'Please select a file to upload.'
      case 'FILE_TOO_LARGE':
        return `The file "${fileName}" is too large. Please choose a file smaller than ${Math.round(this.options.maxFileSize / (1024 * 1024))}MB.`
      case 'INVALID_EXTENSION':
        return `The file "${fileName}" is not a supported format. Please use JSON or TXT files.`
      case 'EMPTY_FILE':
        return `The file "${fileName}" is empty. Please choose a file with JSON content.`
      case 'READ_ERROR':
        return `Could not read the file "${fileName}". Please make sure the file is not corrupted and try again.`
      case 'READ_ABORTED':
        return `File reading was cancelled. Please try again.`
      case 'PROCESSING_ERROR':
        return `An error occurred while processing "${fileName}". Please try again.`
      case 'NO_FILES':
        return 'No files were provided. Please drag and drop a JSON file.'
      case 'NO_VALID_FILES':
        return 'No valid JSON files found. Please drag and drop a .json or .txt file.'
      default:
        return error || 'An unknown error occurred while processing the file.'
    }
  }
}

// Export a default instance for convenience
export const fileHandler = new FileHandler()
